import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, collectPeSessions, getPeSessions, type PeSessionUser, type PeSessions } from "@/lib/api";
import { timeAgo } from "@/lib/deployments";
import { METHOD, dayOf, fmtSpan, howItEnded, timeOf } from "@/lib/sessions";
import { STATUS_HUE } from "@/lib/statusColors";
import { Avatar } from "@/components/Avatar";

/** Sessions shown before "Show all". */
const FIRST_ROWS = 30;

/**
 * A PE app's Sessions tab: its users A-Z, and for the one selected, each
 * sign-in with when it ended. Copied from the app every ten minutes (see
 * backend/src/deployments/sessions.service.ts).
 */
export function UserSessions({ serviceId }: { serviceId: string }) {
  const [chosen, setChosen] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["pe-sessions", serviceId],
    queryFn: () => getPeSessions(serviceId),
    refetchInterval: 60_000,
  });
  const collect = useMutation({
    mutationFn: collectPeSessions,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pe-sessions", serviceId] }),
  });

  if (isLoading) {
    return (
      <div className="py-16 text-center text-text-muted">
        <i className="fa-solid fa-circle-notch fa-spin text-xl" />
      </div>
    );
  }
  if (isError) return <p className="text-sm text-text-secondary">Could not load sessions.</p>;
  if (!data?.configured) return <NotConnected />;

  const users = data.users ?? [];
  const current = users.filter((u) => u.isActive);
  // Until someone picks, the first current user who has signed in.
  const selected =
    users.find((u) => u.userId === chosen) ??
    current.find((u) => u.sessions.length) ??
    current[0] ??
    users[0] ??
    null;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <p className="text-sm text-text-secondary">
          {current.length} {current.length === 1 ? "user" : "users"}
          {users.some((u) => u.online) && ` · ${users.filter((u) => u.online).length} online now`}
        </p>
        <button
          onClick={() => collect.mutate()}
          disabled={collect.isPending}
          title="Copy sign-ins from the app now instead of waiting for the next ten-minute run"
          className="ml-auto rounded-token-md border border-border px-3 py-1.5 text-sm text-text-secondary transition-colors hover:border-accent hover:text-text-heading disabled:opacity-50"
        >
          <i className={`fa-solid ${collect.isPending ? "fa-circle-notch fa-spin" : "fa-rotate-right"} mr-1.5 text-xs`} />
          Copy now
        </button>
      </div>
      {collect.isError && (
        <p className="mb-4 rounded-token-md bg-danger/10 px-3 py-2 text-sm text-danger">
          <i className="fa-solid fa-triangle-exclamation mr-1.5" />
          {collect.error instanceof ApiError ? collect.error.message : "Copying failed"}
        </p>
      )}

      {users.length === 0 ? (
        <div className="mb-4 rounded-token-lg border border-dashed border-border px-6 py-12 text-center">
          <p className="text-sm text-text-secondary">No users copied from the app yet.</p>
          {!data.sync && (
            <p className="mt-1 text-xs text-text-muted">That happens every ten minutes, or use Copy now.</p>
          )}
        </div>
      ) : (
        <div className="mb-4 grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
          <UserList users={users} selectedId={selected?.userId ?? null} onSelect={setChosen} />
          {selected && <UserDetail key={selected.userId} user={selected} />}
        </div>
      )}
      <Coverage sync={data.sync ?? null} />
    </div>
  );
}

function OnlineDot() {
  return (
    <span
      className="h-2 w-2 shrink-0 animate-pulse rounded-full"
      style={{ background: STATUS_HUE.green }}
      title="Online now"
    />
  );
}

function UserList({
  users,
  selectedId,
  onSelect,
}: {
  users: PeSessionUser[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  // Users who can no longer sign in sit in a folded section at the bottom.
  const [showGone, setShowGone] = useState(false);
  const current = users.filter((u) => u.isActive);
  const gone = users.filter((u) => !u.isActive);

  const row = (u: PeSessionUser) => {
    const active = u.userId === selectedId;
    return (
      <li key={u.userId}>
        <button
          onClick={() => onSelect(u.userId)}
          aria-current={active || undefined}
          className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
            active ? "bg-accent-muted" : "hover:bg-bg-secondary"
          }`}
        >
          <Avatar name={u.name} size={28} />
          <span className="min-w-0 flex-1">
            <span
              className={`block truncate text-sm font-medium ${u.isActive ? "text-text-heading" : "text-text-muted"}`}
            >
              {u.name}
            </span>
            <span className="block truncate text-xs text-text-muted">{u.email}</span>
          </span>
          {u.online ? (
            <OnlineDot />
          ) : (
            <span className="shrink-0 text-xs text-text-muted">
              {u.lastSignInAt ? timeAgo(u.lastSignInAt) : "never"}
            </span>
          )}
        </button>
      </li>
    );
  };

  return (
    <nav className="self-start overflow-hidden rounded-token-lg border border-border bg-bg-card shadow-token-sm">
      <ul className="divide-y divide-border-light">{current.map(row)}</ul>
      {gone.length > 0 && (
        <>
          <button
            onClick={() => setShowGone(!showGone)}
            aria-expanded={showGone}
            title="Users the app has deleted or deactivated: they can no longer sign in"
            className={`flex w-full items-center gap-2 bg-bg-secondary px-4 py-2 text-left text-xs font-medium text-text-muted transition-colors hover:text-text-heading ${
              current.length ? "border-t border-border" : ""
            }`}
          >
            <i className={`fa-solid ${showGone ? "fa-chevron-down" : "fa-chevron-right"} w-3 text-[0.6rem]`} />
            Deleted or deactivated
            <span className="ml-auto tabular-nums">{gone.length}</span>
          </button>
          {showGone && <ul className="divide-y divide-border-light border-t border-border-light">{gone.map(row)}</ul>}
        </>
      )}
    </nav>
  );
}

const th = "px-3 py-2 text-left text-xs font-medium text-text-muted";
const td = "px-3 py-2 text-text-secondary";

function UserDetail({ user: u }: { user: PeSessionUser }) {
  const [all, setAll] = useState(false);
  const rows = all ? u.sessions : u.sessions.slice(0, FIRST_ROWS);

  return (
    <section className="min-w-0 overflow-hidden rounded-token-lg border border-border bg-bg-card shadow-token-sm">
      <header className="flex flex-wrap items-center gap-3 px-4 pt-4 pb-3">
        <Avatar name={u.name} size={36} />
        <div className="min-w-0">
          <h3 className="truncate font-display text-base font-semibold text-text-heading">{u.name}</h3>
          <p className="truncate text-xs text-text-muted">
            {[u.email, u.role, !u.isActive && "deactivated"].filter(Boolean).join(" · ")}
          </p>
        </div>
        <p className="ml-auto text-xs text-text-secondary">
          {u.sessions.length} {u.sessions.length === 1 ? "session" : "sessions"}
          {u.lastSignInAt && ` · last signed in ${timeAgo(u.lastSignInAt)}`}
        </p>
      </header>

      {u.sessions.length === 0 ? (
        <p className="border-t border-border-light px-4 py-8 text-center text-sm text-text-secondary">
          No sign-ins recorded.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-y border-border">
              <tr>
                <th className={`${th} pl-4`}>Day</th>
                <th className={th}>Logged in</th>
                <th className={th}>Logged out</th>
                <th className={`${th} text-right`}>Duration</th>
                <th className={th}>Method</th>
                <th className={`${th} pr-4`}>How it ended</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-light">
              {rows.map((s) => {
                // An end on a later day than the start carries its own date.
                const end =
                  s.end && (dayOf(s.end) === dayOf(s.startedAt) ? timeOf(s.end) : `${dayOf(s.end)}, ${timeOf(s.end)}`);
                return (
                  <tr key={s.id}>
                    <td className={`${td} whitespace-nowrap pl-4 font-medium text-text-heading`}>{dayOf(s.startedAt)}</td>
                    <td className={`${td} tabular-nums`}>{timeOf(s.startedAt)}</td>
                    <td
                      className={`${td} whitespace-nowrap tabular-nums`}
                      title={
                        s.state === "closed"
                          ? "Last seen: the tab stopped refreshing then, so it closed up to 15 minutes later"
                          : undefined
                      }
                    >
                      {s.state === "online" ? "–" : s.state === "closed" ? `~${end}` : end}
                    </td>
                    <td className={`${td} whitespace-nowrap text-right tabular-nums`}>
                      {fmtSpan(s.durationMs)}
                      {s.state === "online" && " so far"}
                    </td>
                    <td className={td}>
                      {s.method === "break-glass" && (
                        <i className="fa-solid fa-triangle-exclamation mr-1.5" style={{ color: STATUS_HUE.orange }} />
                      )}
                      {METHOD[s.method] ?? s.method}
                    </td>
                    <td className={`${td} pr-4`}>
                      <span className="flex items-center gap-2">
                        {s.state === "online" && <OnlineDot />}
                        {howItEnded(s)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {u.sessions.length > FIRST_ROWS && (
            <button
              onClick={() => setAll(!all)}
              className="w-full border-t border-border-light px-4 py-2 text-sm text-text-secondary hover:text-text-heading"
            >
              {all ? "Show fewer" : `Show all ${u.sessions.length}`}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

/** How far back the history goes, and how the last copy went. */
function Coverage({ sync }: { sync: PeSessions["sync"] | null }) {
  const since = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return (
    <section className="text-xs text-text-muted">
      {sync ? (
        <p>
          Full history since {since(sync.firstRunAt)} · last copied {timeAgo(sync.lastRunAt)}
          {sync.lastError && (
            <span className="ml-2" style={{ color: STATUS_HUE.red }}>
              <i className="fa-solid fa-triangle-exclamation mr-1" />
              Last copy failed: {sync.lastError}
            </span>
          )}
        </p>
      ) : (
        <p>Not copied yet.</p>
      )}
      <p className="mt-1">
        Copied from the app every ten minutes. The app deletes expired sessions, so before the first
        copy only the ones it still had are here. Times are in your time zone.
      </p>
    </section>
  );
}

function NotConnected() {
  return (
    <div className="rounded-token-lg border border-dashed border-border bg-bg-card/40 px-6 py-12 text-center">
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-token-lg bg-accent-muted text-xl text-accent">
        <i className="fa-solid fa-key" />
      </div>
      <h3 className="mt-4 font-display text-lg font-semibold text-text-heading">Connect the app</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-text-secondary">
        Set the same random secret (32+ characters) as{" "}
        <code className="font-mono text-xs">PORTAL_SERVICE_KEY</code> on the PE app and{" "}
        <code className="font-mono text-xs">PE_SERVICE_KEY</code> on the portal, then redeploy both.
      </p>
    </div>
  );
}
